import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CalendarDays, MapPin, Megaphone, Check, Plus, Heart, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { PushBanner } from "@/components/PushBanner";
import { EventCard } from "@/components/EventCard";
import { FeedCard } from "@/components/FeedCard";
import { CategoryFilter } from "@/components/CategoryFilter";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { toHijri } from "@/lib/hijri";
import api from "@/lib/api";
import { isGuestFollowing, toggleGuestFollow } from "@/lib/guest";
import { toast } from "sonner";

export default function TenantHome() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [mosques, setMosques] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [membership, setMembership] = useState({ joined: false, followed_mosques: [] });
  const [notFound, setNotFound] = useState(false);
  const [cat, setCat] = useState("all");
  const [guestFollow, setGuestFollow] = useState(false);

  useEffect(() => {
    if (!user) setGuestFollow(isGuestFollowing(slug));
  }, [slug, user]);

  useEffect(() => {
    api.get(`/associations/${slug}`).then(({ data }) => setAssoc(data)).catch(() => setNotFound(true));
    api.get(`/associations/${slug}/mosques`).then(({ data }) => setMosques(data)).catch(() => {});
    api.get(`/associations/${slug}/events`, { params: { upcoming: true, limit: 30 } }).then(({ data }) => setEvents(data)).catch(() => {});
    api.get(`/associations/${slug}/announcements`).then(({ data }) => setAnnouncements(data)).catch(() => {});
    api.get(`/associations/${slug}/feed`).then(({ data }) => setFeedItems(data.items)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (user) api.get(`/associations/${slug}/membership`).then(({ data }) => setMembership(data)).catch(() => {});
  }, [slug, user]);

  const join = async () => {
    if (!user) {
      const nowFollowing = toggleGuestFollow(slug);
      setGuestFollow(nowFollowing);
      toast.success(nowFollowing ? t("guest_followed_toast") : t("guest_unfollowed_toast"));
      return;
    }
    await api.post(`/associations/${slug}/join`);
    setMembership((m) => ({ ...m, joined: true }));
    toast.success(t("joined"));
  };

  const followMosque = async (mosqueId) => {
    if (!user) return navigate("/login");
    const { data } = await api.post(`/associations/${slug}/mosques/${mosqueId}/follow`);
    setMembership((m) => ({
      joined: true,
      followed_mosques: data.following ? [...m.followed_mosques, mosqueId] : m.followed_mosques.filter((id) => id !== mosqueId),
    }));
  };

  if (notFound) return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <p className="text-center text-muted-foreground pt-32">404 — Association introuvable</p>
    </div>
  );
  if (!assoc) return <div className="min-h-screen bg-background" />;

  const today = new Date();
  const hijri = toHijri(today, assoc.hijri_offset || 0);
  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const filteredEvents = cat === "all" ? events : events.filter((e) => (e.category || "other") === cat);

  return (
    <div className="min-h-screen bg-background">
      <Navbar assoc={assoc} />
      <PushBanner />
      <div className="relative h-52 sm:h-64 bg-muted overflow-hidden">
        {assoc.banner && <img src={assoc.banner} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 flex items-end justify-between gap-4">
            <div className="text-white">
              <p className="text-[11px] font-mono-data uppercase tracking-[0.2em] opacity-80">/{assoc.slug}</p>
              <h1 className="font-heading font-extrabold text-2xl sm:text-4xl tracking-tight" data-testid="tenant-name">{assoc.name}</h1>
              {assoc.city && <p className="text-sm opacity-80 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{assoc.city}{assoc.country ? `, ${assoc.country}` : ""}</p>}
            </div>
            <Button
              data-testid="join-association-btn"
              onClick={join}
              disabled={user ? membership.joined : false}
              className="rounded-full active:scale-95 shrink-0"
              style={{ background: assoc.primary_color, color: "#fff" }}
            >
              {user
                ? (membership.joined ? <><Check className="h-4 w-4 mr-1.5" />{t("joined")}</> : <><Plus className="h-4 w-4 mr-1.5" />{t("join")}</>)
                : (guestFollow ? <><Check className="h-4 w-4 mr-1.5" />{t("following")}</> : <><Heart className="h-4 w-4 mr-1.5" />{t("follow")}</>)}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border border-border rounded-lg px-5 py-4 bg-card">
          <div>
            <p className="font-heading font-bold capitalize">{today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            <p className="text-sm font-mono-data text-muted-foreground" data-testid="tenant-hijri-date">{hijri.label} AH</p>
          </div>
          <div className="flex gap-2">
            <Link to={`/${slug}/gallery`} data-testid="goto-gallery-btn">
              <Button variant="outline" className="rounded-full active:scale-95">
                <Images className="h-4 w-4 mr-2" /> {t("gallery")}
              </Button>
            </Link>
            <Link to={`/${slug}/calendar`} data-testid="goto-calendar-btn">
              <Button variant="outline" className="rounded-full active:scale-95">
                <CalendarDays className="h-4 w-4 mr-2" /> {t("calendar")}
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold text-lg">{t("upcoming_events")}</h2>
              <Link to={`/${slug}/calendar`} className="text-sm text-accent hover:underline">{t("see_all")}</Link>
            </div>
            <div className="mb-4">
              <CategoryFilter events={events} value={cat} onChange={setCat} />
            </div>
            <div className="space-y-3" data-testid="upcoming-events-list">
              {filteredEvents.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">{t("no_events")}</p>}
              {filteredEvents.slice(0, 6).map((e) => (
                <EventCard key={e.id} event={e} slug={slug} hijriOffset={assoc.hijri_offset || 0} />
              ))}
            </div>

            {feedItems.length > 0 && (
              <div className="mt-12">
                <h2 className="font-heading font-bold text-lg mb-5">{t("feed")}</h2>
                <div className="space-y-5" data-testid="tenant-feed-list">
                  {feedItems.map((item) => (
                    <FeedCard key={`${item.type}-${item.id}`} item={item} onDelete={(id) => setFeedItems((f) => f.filter((x) => x.id !== id))} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-10">
            <div>
              <h2 className="font-heading font-bold text-lg mb-5 flex items-center gap-2"><Megaphone className="h-4 w-4 text-accent" />{t("announcements")}</h2>
              <div className="space-y-3" data-testid="announcements-list">
                {announcements.map((a) => (
                  <div key={a.id} className="border border-border rounded-lg p-4 bg-card">
                    <p className="font-heading font-semibold text-sm">{a.title}</p>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{a.content}</p>
                    <p className="text-[10px] font-mono-data text-muted-foreground mt-2">
                      {new Date(a.created_at).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading font-bold text-lg mb-5">{t("mosques")}</h2>
              <div className="space-y-3" data-testid="mosques-list">
                {mosques.map((m) => (
                  <div key={m.id} className="border border-border rounded-lg overflow-hidden bg-card hover-lift">
                    <Link to={`/${slug}/mosques/${m.id}`} data-testid="mosque-card">
                      {m.photo && <img src={m.photo} alt={m.name} className="w-full h-28 object-cover" />}
                      <div className="p-4 pb-2">
                        <p className="font-heading font-bold flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} />
                          {m.name}
                        </p>
                        {m.address && <p className="text-xs text-muted-foreground mt-1">{m.address}</p>}
                      </div>
                    </Link>
                    <div className="px-4 pb-3">
                      <Button
                        variant={membership.followed_mosques.includes(m.id) ? "secondary" : "outline"}
                        size="sm"
                        data-testid="follow-mosque-btn"
                        onClick={() => followMosque(m.id)}
                        className="rounded-full h-7 text-xs active:scale-95"
                      >
                        <Heart className={`h-3 w-3 mr-1.5 ${membership.followed_mosques.includes(m.id) ? "fill-current text-red-500" : ""}`} />
                        {membership.followed_mosques.includes(m.id) ? t("following") : t("follow")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border py-8 mt-8">
        <p className="text-center text-xs text-muted-foreground">{t("powered_by")}</p>
      </footer>
    </div>
  );
}
